/* Cortazu cart v3 (drawer + /cart page): delivery strip, "Complete the outfit" row (up to 3 cards), cart errors.
   Works with Hyper's cart flow: adds publish FoxTheme.pubsub cartUpdate with the
   bundled sections, so CartItems re-renders the drawer / main-cart exactly like a product form add.
   The upsell's product-type and gender rules run in Liquid (snippets/cz-cart-upsell.liquid); the
   related fallback below only fetches that snippet re-rendered with Shopify's recommendations.
   After an add the drawer / page re-renders through Hyper, so the row shows the next candidates. */
(() => {
  // Liquid's t filter HTML-escapes plain keys (' becomes &#39;); decode once, the strings are set as text.
  const czDecode = (value) => {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    const el = document.createElement('textarea');
    el.innerHTML = value;
    return el.value;
  };
  const czStrings = () => {
    const strings = window.CzCartStrings || {};
    if (!strings.czDecoded) {
      Object.keys(strings).forEach((key) => {
        strings[key] = czDecode(strings[key]);
      });
      strings.czDecoded = true;
    }
    return strings;
  };

  // One polite live region for all cart messages (the visible messages sit in re-rendered markup).
  const czAnnounce = (message) => {
    if (!message) return;
    let region = document.getElementById('CzCartLive');
    if (!region) {
      region = document.createElement('div');
      region.id = 'CzCartLive';
      region.className = 'visually-hidden';
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('role', 'status');
      document.body.appendChild(region);
    }
    region.textContent = '';
    setTimeout(() => {
      region.textContent = message;
    }, 50);
  };

  const czErrorEl = (message) => {
    const el = document.createElement('p');
    el.className = 'cz-cart-error';
    el.textContent = message;
    return el;
  };

  // NL public holidays on which orders do not ship (claim C-10: business days, excl. holidays).
  // Confirm with Ops / Monta and extend yearly.
  const CZ_HOLIDAYS = [
    '2026-01-01', '2026-04-06', '2026-04-27', '2026-05-14', '2026-05-25', '2026-12-25', '2026-12-26',
    '2027-01-01', '2027-03-29', '2027-04-27', '2027-05-06', '2027-05-17', '2027-12-25', '2027-12-26'
  ];
  const CUTOFF_HOUR = 17;

  const amsterdamNow = () => {
    const parts = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Amsterdam',
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23'
    })
      .formatToParts(new Date())
      .forEach((part) => {
        parts[part.type] = part.value;
      });
    return parts;
  };

  if (!customElements.get('cz-cart-delivery')) {
    class CzCartDelivery extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        this.update();
        // Recompute on every opening, so a drawer left open past 17:00 is correct next time.
        this.closest('cart-drawer')?.addEventListener('toggle', () => this.update(), {
          signal: this.abortController.signal
        });
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      update() {
        const today = this.querySelector('[data-cz-ship-today]');
        const next = this.querySelector('[data-cz-ship-next]');
        if (!today || !next) return;
        try {
          const now = amsterdamNow();
          const ymd = `${now.year}-${now.month}-${now.day}`;
          const isBusinessDay = !['Sat', 'Sun'].includes(now.weekday) && !CZ_HOLIDAYS.includes(ymd);
          const shipsToday = isBusinessDay && Number(now.hour) < CUTOFF_HOUR;
          today.hidden = !shipsToday;
          next.hidden = shipsToday;
          this.hidden = false;
        } catch (error) {
          this.hidden = true;
        }
        window.czCartTopFit?.();
      }
    }
    customElements.define('cz-cart-delivery', CzCartDelivery);
  }

  // 28 Oct 2026 (owner, r58): keep the cart drawer top bar on ONE line. Try the long ship text, then the short one
  // ("Ships today"), then returns/warranty only; wrap only if even that does not fit. Re-run on open and resize.
  if (!window.czCartTopFit) {
    const fitOne = (bar) => {
      const line = bar.querySelector('.cz-cart-top__line');
      if (!line || !line.clientWidth) return;
      const over = () => line.scrollWidth > line.clientWidth + 1;
      bar.classList.remove('is-short', 'is-min', 'is-wrap');
      bar.setAttribute('data-cz-fit', '');
      if (!over()) return;
      bar.classList.add('is-short');
      if (!over()) return;
      bar.classList.add('is-min');
      if (!over()) return;
      bar.classList.add('is-wrap');
    };
    window.czCartTopFit = () => document.querySelectorAll('[data-cz-cart-top]').forEach(fitOne);
    const later = () => requestAnimationFrame(() => requestAnimationFrame(window.czCartTopFit));
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(window.czCartTopFit, 150);
    }, { passive: true });
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('cart-drawer').forEach((d) => d.addEventListener('toggle', later));
      later();
    });
    if (document.readyState !== 'loading') later();
    document.fonts?.ready?.then(later);
  }

  if (!customElements.get('cz-cart-upsell')) {
    class CzCartUpsell extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;
        this.addEventListener('click', this.onClick.bind(this), { signal });

        // data-url is only set when the metafields gave fewer than 3 cards and the related fallback applies.
        if (!this.dataset.url) return;

        const drawer = this.closest('cart-drawer');
        if (!drawer || drawer.open) {
          this.load();
        } else {
          drawer.addEventListener('toggle', () => drawer.open && this.load(), { signal });
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      async load() {
        if (this.loading || this.loaded) return;
        this.loading = true;
        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('cz-cart-upsell');
          if (!this.isConnected) return;
          const freshCount = fresh ? fresh.querySelectorAll('.cz-cart-upsell__card').length : 0;
          const currentCount = this.querySelectorAll('.cz-cart-upsell__card').length;
          // The fetched row starts with the same metafield cards, so only swap when it adds cards.
          if (freshCount > currentCount) {
            const row = this.querySelector('.cz-cart-upsell__row');
            const scrollLeft = row ? row.scrollLeft : 0;
            this.innerHTML = fresh.innerHTML;
            this.dataset.count = String(freshCount);
            const newRow = this.querySelector('.cz-cart-upsell__row');
            if (newRow && scrollLeft) newRow.scrollLeft = scrollLeft;
            this.hidden = false;
          } else if (!currentCount && !this.querySelector('[data-cz-build]')) {
            this.hidden = true;
          }
          this.loaded = true;
        } catch (error) {
          console.error('[cz-cart-upsell]', error);
          if (!this.querySelector('.cz-cart-upsell__card, [data-cz-build]')) this.hidden = true;
        } finally {
          this.loading = false;
        }
      }

      onClick(event) {
        const addButton = event.target.closest('[data-cz-upsell-add]');
        if (addButton) {
          event.preventDefault();
          this.add(addButton);
          return;
        }
        const quickViewLink = event.target.closest('[data-cz-upsell-quickview]');
        // With quick-view.js on the page the modal binds this link itself (aria-controls);
        // without it the link simply opens the product page.
        if (quickViewLink && customElements.get('quick-view-modal')) {
          event.preventDefault();
        }
      }

      showError(message) {
        const errorEl = this.querySelector('.cz-cart-upsell__error');
        if (!errorEl) return;
        const textEl = errorEl.querySelector('[data-cz-error-text]') || errorEl;
        textEl.textContent = message || '';
        errorEl.hidden = !message;
        if (message) czAnnounce(message);
      }

      async add(button) {
        if (button.getAttribute('aria-disabled') === 'true') return;
        const variantId = Number(button.dataset.variantId);
        if (!variantId) return;

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('is-loading');
        this.showError('');

        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
        );

        let linesUpdate = null;
        try {
          linesUpdate = FoxTheme.storefrontEvents?.start(this, 'CartLinesUpdateEvent', {
            action: 'add',
            context: 'dialog',
            lines: [{ merchandiseId: String(variantId), quantity: 1 }]
          });
        } catch (e) {
          linesUpdate = null;
        }

        try {
          const response = await fetch(`${FoxTheme.routes.cart_add_url}.js`, {
            ...FoxTheme.utils.fetchConfig('javascript'),
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
              sections,
              sections_url: window.location.pathname
            })
          });
          const parsed = await response.json();

          if (parsed.status) {
            try {
              FoxTheme.storefrontEvents?.resolveCart(linesUpdate, null, FoxTheme.storefrontEvents.getAjaxError(parsed));
            } catch (e) {}
            // 422 = sold out / no more stock: our friendly sentence instead of Shopify's technical one.
            this.showError((parsed.status === 422 && czStrings().add) || this.dataset.errorText);
            return;
          }

          const cartJson = await (
            await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
          ).json();
          cartJson.sections = parsed.sections;

          try {
            FoxTheme.storefrontEvents?.resolveCart(linesUpdate, cartJson);
          } catch (e) {}

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
          const addedItem = Array.isArray(parsed.items) ? parsed.items[0] : parsed;
          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: addedItem } }));
        } catch (error) {
          try {
            FoxTheme.storefrontEvents?.failCart(this, linesUpdate, error);
          } catch (e) {}
          console.error('[cz-cart-upsell]', error);
          this.showError(this.dataset.errorText);
        } finally {
          if (button.isConnected) {
            button.removeAttribute('aria-disabled');
            button.classList.remove('is-loading');
          }
        }
      }
    }
    customElements.define('cz-cart-upsell', CzCartUpsell);
  }

  /* ---------- Cart errors (drawer + page) ---------- */

  // Inline message under a cart line, after the re-render.
  const czShowLineError = (line, message) => {
    if (!message) return;
    const lineEl = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
    const slot = lineEl && (lineEl.querySelector('[data-cz-line-error]') || lineEl.querySelector('.cart-item__details') || lineEl);
    if (slot) {
      slot.querySelector(':scope > .cz-cart-error')?.remove();
      slot.appendChild(czErrorEl(message));
    }
    czAnnounce(message);
  };

  // Message in the drawer header (or under the /cart title): add-to-cart errors.
  const czShowAlert = (message) => {
    if (!message) return;
    const drawer = document.querySelector('cart-drawer');
    const alert =
      (drawer && drawer.querySelector('[data-cz-cart-alert]')) || document.querySelector('.cz-cart-page [data-cz-cart-alert]');
    if (alert) {
      alert.replaceChildren(czErrorEl(message));
      alert.hidden = false;
    }
    czAnnounce(message);
  };
  const czClearAlerts = () => {
    document.querySelectorAll('[data-cz-cart-alert]').forEach((alert) => {
      alert.hidden = true;
      alert.replaceChildren();
    });
  };

  const czInitErrors = () => {
    if (window.CzCartErrorsReady || !window.FoxTheme) return;
    window.CzCartErrorsReady = true;
    const strings = czStrings();

    // Friendly discount-code messages (Hyper's CartDiscount reads these).
    if (FoxTheme.cartStrings) {
      if (strings.discountInvalid) FoxTheme.cartStrings.applyDiscountError = strings.discountInvalid;
      if (strings.discountDuplicate) FoxTheme.cartStrings.duplicateDiscountError = strings.discountDuplicate;
    }

    // Add errors (product form, quick view, sticky ATC): Hyper opens the drawer, we explain why.
    document.addEventListener('product-ajax:error', () => czShowAlert(czStrings().add));
    document.addEventListener('product-ajax:added', czClearAlerts);

    // "Change" on a cart line (22 Oct 2026): the link opens the quick view of that product (Hyper binds it
    // through aria-controls); the modal remembers which line it replaces. When the buyer adds from that
    // modal, the old line goes (or, if the same variant was picked, its quantity is set back) and the
    // cart re-renders. Closing the modal without adding changes nothing.
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-cz-cart-change]');
      if (!link) return;
      if (!customElements.get('quick-view-modal')) return; // no quick view: the link opens the product page
      event.preventDefault();
      const modalId = link.getAttribute('aria-controls');
      document.querySelectorAll('quick-view-modal[data-cz-replace]').forEach((m) => delete m.dataset.czReplace);
      const modal = modalId && document.getElementById(modalId);
      if (modal) {
        modal.dataset.czReplace = JSON.stringify({
          key: link.dataset.lineKey,
          variantId: link.dataset.variantId,
          quantity: parseInt(link.dataset.quantity, 10) || 1
        });
      }
    });
    document.addEventListener('product-ajax:added', async (event) => {
      const modal = document.querySelector('quick-view-modal[open][data-cz-replace], quick-view-modal[active][data-cz-replace]');
      if (!modal) return;
      let replace = null;
      try {
        replace = JSON.parse(modal.dataset.czReplace);
      } catch (e) {}
      delete modal.dataset.czReplace;
      if (!replace || !replace.key) return;
      const added = event.detail?.product;
      const sameVariant = added && String(added.variant_id) === String(replace.variantId);
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
      );
      try {
        const response = await fetch(`${FoxTheme.routes.cart_change_url}`, {
          ...FoxTheme.utils.fetchConfig(),
          body: JSON.stringify({
            id: replace.key,
            quantity: sameVariant ? replace.quantity : 0,
            sections,
            sections_url: window.location.pathname
          })
        });
        const cartJson = await response.json();
        if (cartJson.status || cartJson.errors) throw new Error(cartJson.description || cartJson.message || 'change failed');
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
      } catch (error) {
        console.error('[cz-cart change]', error);
        czShowAlert(czStrings().update);
      }
    });
    const drawer = document.querySelector('cart-drawer');
    drawer?.addEventListener('toggle', () => {
      if (!drawer.open) czClearAlerts();
    });

    // Quantity changes: Shopify answers a quantity above stock with a 422 and caps the line,
    // which Hyper's CartItems ignores (the line keeps showing the asked quantity). Same flow as
    // Hyper's updateQuantity, plus: on an error, re-render from the real cart and say what happened.
    customElements.whenDefined('cart-items').then(() => {
      const CartItemsClass = customElements.get('cart-items');
      if (!CartItemsClass || CartItemsClass.prototype.czPatched) return;
      CartItemsClass.prototype.czPatched = true;

      CartItemsClass.prototype.updateQuantity = function (line, quantity, name, target) {
        this.showLoader(line);

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({ line, quantity, sections: sectionsToBundle });
        const linesUpdate = this.startCartLinesUpdate(line, quantity);
        const requested = parseInt(quantity, 10);

        const fail = (message) => {
          this.hideLoader(line);
          if (target && target.defaultValue !== undefined) target.value = target.defaultValue;
          czShowLineError(line, message);
        };

        fetch(`${FoxTheme.routes.cart_change_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then(async (parsedState) => {
            const errorMessage = FoxTheme.storefrontEvents?.getAjaxError(parsedState);
            try {
              FoxTheme.storefrontEvents?.resolveCart(
                linesUpdate,
                errorMessage ? null : parsedState,
                errorMessage,
                (ajaxCart) => ajaxCart.items[line - 1]?.quantity !== linesUpdate?.previousQuantity
              );
            } catch (e) {}

            if (!parsedState.status && !parsedState.errors) {
              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                cart: parsedState,
                target,
                line,
                name
              });
              return;
            }

            // Error: read the cart as it stands now (with the sections) and re-render from it.
            let cartJson = null;
            try {
              const response = await fetch(`${FoxTheme.routes.cart_update_url}`, {
                ...FoxTheme.utils.fetchConfig(),
                body: JSON.stringify({ sections: sectionsToBundle, sections_url: window.location.pathname })
              });
              cartJson = await response.json();
            } catch (e) {
              cartJson = null;
            }
            if (!cartJson || cartJson.status || !cartJson.sections) {
              fail(czStrings().update);
              return;
            }

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson, line, name });

            // /cart/change answers {errors}, /cart/change.js {status: 422, description}: both mean stock.
            const stockError = parsedState.status === 422 || Boolean(parsedState.errors);
            const nowInCart = cartJson.items?.[line - 1]?.quantity;
            let message = czStrings().update;
            if (stockError && nowInCart > 0 && requested > nowInCart && czStrings().quantity) {
              message = czStrings().quantity.replace('[quantity]', nowInCart);
            } else if (stockError && czStrings().add) {
              message = czStrings().add;
            }
            czShowLineError(line, message);
          })
          .catch((error) => {
            try {
              FoxTheme.storefrontEvents?.failCart(this, linesUpdate, error);
            } catch (e) {}
            console.error('[cz-cart]', error);
            fail(czStrings().update);
          });
      };
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', czInitErrors);
  } else {
    czInitErrors();
  }
})();

/* Input modality for the cart drawer close (x): Hyper focuses it on open with a plain .focus().
   After a mouse click or tap (Safari does not focus the clicked link, and an add-to-cart button
   loses focus) browsers can still match :focus-visible, so a ring showed on mouse/tap open.
   data-cz-pointer marks pointer use until the next key press; cz-cart.css hides the ring only then.
   Focus itself still moves to the close button for keyboard and screen-reader users. */
(() => {
  if (window.CzInputModality) return;
  window.CzInputModality = true;
  const root = document.documentElement;
  document.addEventListener(
    'pointerdown',
    () => {
      root.setAttribute('data-cz-pointer', '');
    },
    true
  );
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      root.removeAttribute('data-cz-pointer');
    },
    true
  );
})();

/* 28 Oct 2026 (owner, r66): free discount-code field in the cart drawer (sections/cart-drawer.liquid, form[data-cz-dc]).
   Any code: /cart/update.js { discount: "<applied codes>,<new code>" } (Shopify replaces the cart's code list with it),
   then the drawer re-renders through cartUpdate, so the applied code shows as a chip with × (Hyper cart-discount-remove).
   Shopify accepts unknown or non-applicable codes without an error (applicable: false): we then put the previous codes
   back, so no dead code stays on the cart, and show the message in the drawer language (data-err*). */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest && event.target.closest('form[data-cz-dc]');
  if (!form) return;
  event.preventDefault();
  const input = form.querySelector('input[name="discount"]');
  const button = form.querySelector('button[type="submit"]');
  const msg = form.querySelector('[data-cz-dc-msg]');
  const code = (input.value || '').trim();
  const say = (text) => {
    msg.textContent = text || '';
    msg.hidden = !text;
    input.setAttribute('aria-invalid', text ? 'true' : 'false');
    const box = form.closest('details');
    if (text && box) box.open = true;
  };
  if (!code) { input.focus(); return; }
  if (button.getAttribute('aria-busy') === 'true') return;
  say('');
  button.setAttribute('aria-busy', 'true');
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const base = root.endsWith('/') ? root : `${root}/`;
  const post = (discount, sections) => fetch(`${base}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(sections ? { discount, sections, sections_url: window.location.pathname } : { discount })
  }).then((r) => r.json().then((j) => { if (!r.ok || j.status) throw new Error(j.description || `HTTP ${r.status}`); return j; }));
  try {
    const before = await (await fetch(`${base}cart.js`, { headers: { Accept: 'application/json' } })).json();
    const applied = (before.discount_codes || []).filter((d) => d.applicable !== false).map((d) => d.code);
    if (applied.some((c) => c.toLowerCase() === code.toLowerCase())) { say(form.dataset.errDup); return; }
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }));
    const parsed = await post(applied.concat(code).join(','), sections);
    const entry = (parsed.discount_codes || []).find((d) => (d.code || '').toLowerCase() === code.toLowerCase());
    if (!entry || entry.applicable === false) {
      // Not valid / not for this cart: restore the earlier codes (no re-render needed, the cart is as before)
      await post(applied.join(',')).catch(() => {});
      const promo = (form.dataset.promo || '').toLowerCase();
      say(promo && promo === code.toLowerCase() && form.dataset.promoSale ? form.dataset.promoSale : form.dataset.err);
      input.select();
      return;
    }
    const cartJson = await (await fetch(`${base}cart.js`, { headers: { Accept: 'application/json' } })).json();
    cartJson.sections = parsed.sections;
    if (window.FoxTheme && FoxTheme.pubsub) FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
  } catch (error) {
    console.error('[cz-cart-discount]', error);
    say(form.dataset.errNet || form.dataset.err);
  } finally {
    if (button.isConnected) button.removeAttribute('aria-busy');
  }
});
// Typing again clears the message
document.addEventListener('input', (event) => {
  const form = event.target.closest && event.target.closest('form[data-cz-dc]');
  const msg = form && form.querySelector('[data-cz-dc-msg]');
  if (msg && !msg.hidden) { msg.hidden = true; msg.textContent = ''; event.target.setAttribute('aria-invalid', 'false'); }
});

/* 28 Oct 2026 (owner, r62): "★ 4.8 · 2,869 reviews" under the cart drawer totals (claim C-05, DYNAMIC).
   Same source as the header USP bar (sections/cortazu-usp-bar.liquid): the store-wide count comes live from Stamped and
   is cached there in sessionStorage ('cz-stamped-total', 6 h); the rating is the USP bar's own rating text.
   Nothing is typed in here: without that source (or before it has a count) the line stays hidden.
   A custom element, so it fills itself again after every drawer re-render (the footer is replaced on each cart change). */
if (!customElements.get('cz-cart-rating')) {
  const czRatingSource = () => document.querySelector('[data-cz-reviews]');
  const czRatingCount = () => {
    try {
      const cached = JSON.parse(sessionStorage.getItem('cz-stamped-total') || 'null');
      if (cached && cached.n > 0 && Date.now() - cached.t < 6 * 3600 * 1000) return Number(cached.n);
    } catch (e) {}
    return 0;
  };
  class CzCartRating extends HTMLElement {
    connectedCallback() {
      if (this.fill()) return;
      const src = czRatingSource();
      if (!src) return;
      // The USP bar fetches the count once per session; it writes the cache right after updating its text.
      this.observer = new MutationObserver(() => setTimeout(() => { if (this.fill()) this.observer?.disconnect(); }, 0));
      this.observer.observe(src, { childList: true, characterData: true, subtree: true });
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
    }
    fill() {
      const src = czRatingSource();
      const n = czRatingCount();
      if (!src || !n) return false;
      const loc = this.dataset.locale || 'en';
      let rating = ((src.dataset.rating || '').match(/\d+(?:[.,]\d+)?/) || [''])[0];
      if (!rating) return false;
      rating = loc === 'en' ? rating.replace(',', '.') : rating.replace('.', ',');
      let count;
      try { count = n.toLocaleString(loc); } catch (e) { count = String(n); }
      const tpl = this.dataset.tpl && this.dataset.tpl.includes('%N%') ? this.dataset.tpl : '%N% reviews';
      const [before, after] = tpl.split('%N%');
      const el = (cls, text) => { const s = document.createElement('span'); s.className = cls; s.textContent = text; return s; };
      const star = el('cz-cart-rating__star', '★');
      star.setAttribute('aria-hidden', 'true');
      const sep = el('cz-cart-rating__sep', '·');
      sep.setAttribute('aria-hidden', 'true');
      const reviews = el('cz-cart-rating__reviews', '');
      if (before.trim()) reviews.append(before);
      reviews.append(el('cz-cart-rating__n', count));
      if (after) reviews.append(after);
      this.replaceChildren(star, el('cz-cart-rating__r', rating), sep, reviews);
      this.setAttribute('aria-label', `${rating}/5 · ${(before + count + after).trim()}`);
      this.setAttribute('role', 'note');
      this.hidden = false;
      return true;
    }
  }
  customElements.define('cz-cart-rating', CzCartRating);
}

/* 28 Oct 2026 (r62) / 29 Oct 2026 (Wouter: "remove the autoscroll from the cart, not up, not down"):
   a new opening of the drawer starts at the top (newest item first), unless the shopper already scrolled, tapped or
   pressed a key in it. No scrolling after that: the r66 re-render watcher and the 15.68 "back to top after an add"
   are removed; cart updates keep the body's scroll position (block below). */
(() => {
  // r71 (29 Oct): <cart-drawer> is a custom element with an [open] attribute and fires no 'toggle', so watch the attribute.
  // Until the shopper touches the drawer (max 6 s after opening), any scroll of the body they did not make (Hyper's focus on open) is undone.
  const BODY = '.drawer__body.flex-col';
  const onOpen = (drawer) => {
    let touched = false;
    const ac = new AbortController();
    ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach((t) => drawer.addEventListener(t, () => { touched = true; }, { passive: true, signal: ac.signal }));
    const reset = () => { const b = drawer.querySelector(BODY); if (!touched && drawer.hasAttribute('open') && b && b.scrollTop) b.scrollTop = 0; };
    drawer.addEventListener('scroll', reset, { capture: true, passive: true, signal: ac.signal });
    requestAnimationFrame(reset);
    [120, 350, 700, 1500, 2500].forEach((ms) => setTimeout(reset, ms));
    // r71b: Hyper's re-render + focus after an add lands later than 1.2 s -> keep guarding until the shopper touches
    // the drawer (pointer, wheel, touch, key) or 6 s have passed
    setTimeout(() => { reset(); ac.abort(); }, 6000);
  };
  const bind = (drawer) => {
    if (drawer.dataset.czTopReset) return;
    drawer.dataset.czTopReset = '1';
    let wasOpen = drawer.hasAttribute('open');
    new MutationObserver(() => {
      const isOpen = drawer.hasAttribute('open');
      if (isOpen && !wasOpen) onOpen(drawer);
      wasOpen = isOpen;
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  };
  const all = () => document.querySelectorAll('cart-drawer').forEach(bind);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', all);
  else all();
})();

/* 29 Oct 2026 (Wouter: no autoscroll in the cart): the drawer body keeps its scroll position on add (PDP, quick view,
   upsell "+ Add"), quantity change, removal and discount codes.
   Cause of the jumps: after every cart update Hyper's cart.js (CartItems.onCartUpdate) replaces the body's markup and
   calls FoxTheme.a11y.trapFocus(drawer, <line title / quantity input>), whose plain .focus() makes the browser scroll
   that element into view. Without editing Hyper:
   1. FoxTheme.a11y.trapFocus / removeTrapFocus focus an element inside the cart drawer with { preventScroll: true }
      (same element, same focus trap; only the browser's scroll-into-view is dropped);
   2. every cart request first fires 'cart:grouped-sections' (Hyper and our own code), so the body's scrollTop is saved
      there and put back on 'cart:updated' (fired right after the re-render, before the next paint) and once more on
      the next frame, unless the shopper scrolled in between. */
(() => {
  const BODY = '.drawer__body.flex-col';
  const inDrawer = (el) => !!(el && el.closest && el.closest('cart-drawer'));

  // Calls fn with el.focus temporarily forced to { preventScroll: true }.
  const withoutScroll = (el, fn) => {
    if (!inDrawer(el) || typeof el.focus !== 'function' || Object.prototype.hasOwnProperty.call(el, 'focus')) return fn();
    const nativeFocus = el.focus;
    el.focus = function (options) {
      return nativeFocus.call(this, Object.assign({}, options, { preventScroll: true }));
    };
    try {
      return fn();
    } finally {
      delete el.focus;
    }
  };
  const wrapTrapFocus = () => {
    const a11y = window.FoxTheme && FoxTheme.a11y;
    if (!a11y || typeof a11y.trapFocus !== 'function') return false;
    if (a11y.trapFocus.czNoScroll) return true;
    const trap = a11y.trapFocus;
    const release = a11y.removeTrapFocus;
    const trapWrapped = function (container, elementToFocus = container) {
      // the element only matters when it sits in the cart drawer (also when the container is a quick view inside it)
      return withoutScroll(elementToFocus, () => trap.call(this, container, elementToFocus));
    };
    trapWrapped.czNoScroll = true;
    a11y.trapFocus = trapWrapped;
    if (typeof release === 'function') {
      // closing a quick view opened from the drawer returns focus to its "+"/"Change" in the drawer
      a11y.removeTrapFocus = function (elementToFocus = null) {
        return withoutScroll(elementToFocus, () => release.call(this, elementToFocus));
      };
    }
    return true;
  };
  if (!wrapTrapFocus()) {
    document.addEventListener('DOMContentLoaded', wrapTrapFocus);
    window.addEventListener('load', wrapTrapFocus);
  }

  let saved = null; // [{ drawer, top }]
  let userScrolled = false;
  const markUser = () => { userScrolled = true; };
  ['wheel', 'touchmove', 'keydown'].forEach((t) => document.addEventListener(t, markUser, { passive: true, capture: true }));

  document.addEventListener('cart:grouped-sections', () => {
    wrapTrapFocus();
    saved = [];
    userScrolled = false;
    document.querySelectorAll('cart-drawer').forEach((drawer) => {
      const body = drawer.querySelector(BODY);
      if (drawer.open && body) saved.push({ drawer, top: body.scrollTop });
    });
  }, true);

  document.addEventListener('cart:updated', () => {
    const list = saved;
    saved = null;
    if (!list || !list.length) return;
    const restore = () => {
      if (userScrolled) return;
      list.forEach(({ drawer, top }) => {
        const body = drawer.querySelector(BODY);
        if (drawer.open && body && Math.abs(body.scrollTop - top) > 1) body.scrollTop = top;
      });
    };
    restore();
    requestAnimationFrame(restore);
  });
})();

/* 31 Oct 2026 (audit F10): empty cart drawer = empty state only, no footer ("Total €0 · Checkout · €0", payment
   icons, View cart). Hyper toggles the classes in CartItems.onCartUpdate, but only when the update carries the
   drawer section; this keeps body / footer / empty state in step with the cart count after every cart update
   (pubsub cartUpdate and 'cart:updated'), so removing the last item always ends on the empty state.
   The server render does the same (sections/cart-drawer.liquid, cart.item_count == 0). */
(() => {
  if (window.CzCartEmptySync) return;
  window.CzCartEmptySync = true;
  const sync = (cart) => {
    if (!cart || typeof cart.item_count !== 'number' || cart.errors || cart.status) return;
    const empty = cart.item_count === 0;
    document.querySelectorAll('cart-drawer').forEach((drawer) => {
      drawer.querySelectorAll('[id^="CartDrawerFooter-"], [id^="CartDrawerBody-"]').forEach((el) => {
        el.classList.toggle('hidden', empty);
      });
      drawer.querySelectorAll('[id^="CartDrawerEmpty-"]').forEach((el) => el.classList.toggle('hidden', !empty));
    });
  };
  document.addEventListener('cart:updated', (event) => sync(event.detail && event.detail.cart));
  const subscribe = () => {
    try {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) =>
        requestAnimationFrame(() => sync(event && event.cart))
      );
    } catch (e) {}
  };
  if (window.FoxTheme && FoxTheme.pubsub) subscribe();
  else document.addEventListener('DOMContentLoaded', subscribe);
})();
